import React, { useEffect, useRef, useState } from 'react'
import { Box, Card, Checkbox, Flex, FormField, PrimaryButton, SecondaryButton, space, Text, Tooltip } from '@workday/canvas-kit-react'
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api'
import IncidentCard from './IncidentCard'
import TimeDisplayOption from '../models/TimeDisplayOptions'
import { PrimaryButtonColors } from '../../components/CanvasThemes'
import { AgencyModel } from '../../models/AgencyModel'
import { IncidentFeedModel } from '../../models/IncidentFeedModel'
import { IncidentModel } from '../../models/IncidentModel'
import styled from '@emotion/styled'
import { loopIcon } from '@workday/canvas-system-icons-web'
import { AEDModel, Coordinate, Environment } from '../../models'
import { fetchAEDs } from '../models/WebAppRequests'
import { CognitoUserSession } from 'amazon-cognito-identity-js'

export type IncidentMapViewport = {
  center: Coordinate
  zoomLevel: number
}

export type IncidentMapProps = {
  isSmallScreenWidth: boolean
  timeDisplayOption: TimeDisplayOption
  agencies: AgencyModel[]
  feedData: IncidentFeedModel
  viewport?: IncidentMapViewport
  onSelectIncident: (incident: IncidentModel) => void
  onMapMove: (viewport?: IncidentMapViewport) => void
  env: Environment
  session?: CognitoUserSession
}

const DisplayResponderCard = styled(Card)<{isSmallScreen: boolean}>`
  ${(props) => props.isSmallScreen ? 'bottom: 30px;' : 'top: 9px;' }
  ${(props) => props.isSmallScreen ? 'left: 10px;' : 'right: 60px;' }
  position: absolute;
  z-index: 10;
  opacity: 0.95;
  width: 140px;
  padding-top: 8px;
  padding-bottom: 8px;

  > div {
   margin:0px;
  }
`
const StyledTertiaryButton = styled(SecondaryButton)<{isSmallScreen: boolean}>`
  position: absolute;
  top: 10px;
  ${(props) => props.isSmallScreen ? 'left: calc(50vw - 58px);' : 'left: calc(50vw - 283px);' }
  padding: 10px 20px;
  z-index: 11;
  background: white;
  border: 0px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
`

const minMapZoomLevelForAEDs = 11

const IncidentMap = ({ isSmallScreenWidth, timeDisplayOption, agencies, feedData, viewport, onSelectIncident, onMapMove, session, env }: IncidentMapProps) => {
  const [map, setMap] = useState<any | null>(null)
  const [selectedMarkerID, setSelectedMarkerID] = useState<string | undefined>(undefined)
  const [aeds, setAEDs] = useState<AEDModel[]>()
  const [showAEDs, setShowAEDs] = useState<boolean>(false)
  const [curZoomLevel, setCurZoomLevel] = useState<number>(-1)
  const [curBounds, setCurBounds] = useState<any | null>(null)
  const [showResetButton, setShowResetButton] = useState<boolean>(viewport !== undefined)
  const isInitialLoadRef = useRef(true)

  useEffect(() => {
    if (map == null) return

    if (!viewport) {
      const bounds = new window.google.maps.LatLngBounds()
      const incidents = feedData.mappableIncidents()
      if (incidents.length > 0) {
        incidents.forEach((incident: IncidentModel) => bounds.extend(incident.locationCoordinate))
      } else {
        agencies.forEach(agency => agency.boundaryCornerCoordinates().forEach(coordinate => bounds.extend(coordinate)))
      }
      map.fitBounds(bounds)
    }
  }, [map, feedData, agencies])

  useEffect(() => {
    if (curBounds == null) return

    const northEast = curBounds.getNorthEast()
    const southWest = curBounds.getSouthWest()
    const curViewport = `${southWest.lat()},${northEast.lat()},${southWest.lng()},${northEast.lng()}`
    fetchAEDs(
      curViewport, 
      env, 
      session
    ).then((aeds) => {
      setAEDs(aeds)
    })
  }, [curBounds])

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyCxDiJ-c0tzCGpCH29aVbVubkm6Y5OZeTQ'
  })

  const agencyForAgencyID = (agencyID: string) => {
    return agencies.find(agency => agency.agencyID === agencyID)!
  }

  return (
    <Box flex="1" position='relative'>
      {showResetButton && (
        <StyledTertiaryButton 
          isSmallScreen={isSmallScreenWidth}
          onClick={() => {
            isInitialLoadRef.current = true
            onMapMove()
            setShowResetButton(false)
          }} 
          icon={loopIcon} 
          iconPosition="start"
        >
          Reset Map
        </StyledTertiaryButton>
      )}
      <DisplayResponderCard padding={space.xs} isSmallScreen={isSmallScreenWidth}>
        {curZoomLevel < minMapZoomLevelForAEDs ? (
          <Tooltip title="Zoom In to view AEDs">
            <FormField>
              <Checkbox
                disabled={true}
                checked={showAEDs}
                label='Show AEDs'
              />
            </FormField>
          </Tooltip>
        ) : (
          <FormField>
            <Checkbox
              checked={showAEDs}
              label='Show AEDs'
              onChange={() => setShowAEDs(!showAEDs)}
            />
          </FormField>
        )}
      </DisplayResponderCard>
      {isMapLoaded && (
        <GoogleMap
          onIdle={() => {
            setCurZoomLevel(map?.getZoom())
            if (map?.getZoom() >= minMapZoomLevelForAEDs) {
              setCurBounds(map?.getBounds())
            } else {
              setCurBounds(null)
            }
          }}
          onBoundsChanged={() => {
            if (!isInitialLoadRef.current) {
              onMapMove({
                center: {
                  lat: map?.getCenter()?.lat(),
                  lng: map?.getCenter()?.lng()
                }, 
                zoomLevel: map?.getZoom()
              })
              setShowResetButton(true)
            } else {
              isInitialLoadRef.current = false
            }
          }}
          onLoad={map => {
            setMap(map)
            isInitialLoadRef.current = true
          }}
          mapContainerStyle={{
            height: '100%',
            width: '100%'
          }}
          zoom={viewport?.zoomLevel}
          center={viewport?.center}
          options={isSmallScreenWidth ? {
            rotateControl: false,
            panControl: false,
            streetViewControl: false,
            scaleControl: false,
            mapTypeControlOptions: {
              style: 2
            }
          } : {
            rotateControl: false,
            panControl: false,
            streetViewControl: false,
            scaleControl: false
          }}
        >
          {aeds && showAEDs && aeds.map((aed: AEDModel) => {
            return (
              <Marker
                options={{icon: '/images/AED_pin.png', title: 'AED Location'}}
                position={{lat: aed.latitude, lng: aed.longitude}}
                onClick={event => setSelectedMarkerID(aed.id)}
                key={`marker-${aed.id}`}
              >
                {selectedMarkerID === aed.id && (
                  <InfoWindow onCloseClick={() => setSelectedMarkerID(undefined)}>
                    <Flex flexDirection="column" gap={space.xs} minWidth={100} maxWidth={300}>
                      <Text typeLevel="body.medium" fontWeight="bold">
                        {aed.name}
                      </Text>
                      <Text typeLevel="body.small">
                        {aed.description}
                      </Text>
                    </Flex>
                  </InfoWindow>
                )}
              </Marker>
            )
          })}
          {feedData.mappableIncidents().map((incident: IncidentModel) => {
            const agency = agencyForAgencyID(incident.agencyID)
            return (
              <Marker
                options={{
                  icon: {
                    url: incident.mapPinIconURL(),
                    scaledSize: new google.maps.Size(incident.mapPinIconSize(), incident.mapPinIconSize())
                  },
                  title: 'Incident Location'
                }}
                position={incident.locationCoordinate}
                onClick={event => setSelectedMarkerID(incident.recordID)}
                key={`marker-${incident.recordID}`}
              >
                {selectedMarkerID === incident.recordID && (
                  <InfoWindow onCloseClick={() => setSelectedMarkerID(undefined)}>
                    <Flex flexDirection="column" gap={space.xs}>
                      <IncidentCard incident={incident} agency={agency} timeDisplayOption={timeDisplayOption} showAgencyTitle={agencies.length !== 1} />
                      <PrimaryButton onClick={event => onSelectIncident(incident)} colors={PrimaryButtonColors}>
                        View Details
                      </PrimaryButton>
                    </Flex>
                  </InfoWindow>
                )}
              </Marker>
            )
          })}
        </GoogleMap>
      )}
    </Box>
  )
}

export default IncidentMap
